import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';
import { Trophy, Users, Calendar, ArrowRight, Sparkles, CheckCircle2 } from 'lucide-react';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TGA_LOGO = "https://customer-assets.emergentagent.com/job_853d16bb-77c9-4e1d-85ff-db52f6af924e/artifacts/lsv7codb_Copy%20of%20TGA%20pro%20tour%20logo%20transparent.png";

export default function JoinPersonalEventPage() {
  const navigate = useNavigate();
  const { code } = useParams();
  const { member, setAuth } = useAuth();
  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [joining, setJoining] = useState(false);
  const [form, setForm] = useState({ name: '', email: '' });
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API_URL}/personal-events/invite/${code}`);
        if (!cancelled) setEvent(res.data);
      } catch {
        if (!cancelled) setNotFound(true);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [code]);

  // If already logged in, prefill name+email and let user one-click join
  useEffect(() => {
    if (member) {
      setForm({ name: member.name || '', email: member.email || '' });
    }
  }, [member]);

  const handleJoin = async (e) => {
    e?.preventDefault?.();
    if (!form.name.trim() || !form.email.trim()) {
      toast.error('Name and email are required');
      return;
    }
    setJoining(true);
    try {
      const res = await axios.post(
        `${API_URL}/personal-events/invite/${code}/join`,
        { name: form.name.trim(), email: form.email.trim() }
      );
      const data = res.data;

      // Set auth so the user can immediately access the tournament page
      if (data.access_token && data.member) {
        setAuth(data.member, data.access_token);
      }

      if (data.created_new_account && data.temp_password) {
        toast.success(`Welcome aboard! Your password: ${data.temp_password}`, {
          duration: 12000,
          description: 'Save this — you can change it from your profile.',
        });
      } else {
        toast.success(`Welcome to ${event.name}`);
      }
      navigate(`/tournaments/${data.tournament_id}`);
    } catch (err) {
      const msg = err.response?.data?.detail || 'Could not join the event';
      toast.error(msg);
    } finally {
      setJoining(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <div className="animate-pulse text-primary font-heading text-lg">Loading invitation…</div>
      </div>
    );
  }

  if (notFound) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center px-6">
        <Card className="bg-card border-white/10 max-w-md w-full">
          <CardHeader>
            <CardTitle className="text-white font-heading text-xl">Invitation not found</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-muted-foreground text-sm mb-4">
              This invite link looks broken or the event was deleted by its host.
            </p>
            <Button onClick={() => navigate('/')} className="bg-secondary text-black hover:bg-secondary/90 w-full">
              Go to TGA Pro Tour
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const eventDate = event?.event_date
    ? new Date(event.event_date).toLocaleString(undefined, {
        month: 'short', day: 'numeric', year: 'numeric',
        hour: 'numeric', minute: '2-digit',
      })
    : null;

  return (
    <div className="min-h-screen bg-[#050505]">
      {/* Header strip */}
      <div className="border-b border-white/10 px-6 py-4">
        <img src={TGA_LOGO} alt="TGA Pro Tour" className="h-10 object-contain mx-auto cursor-pointer" onClick={() => navigate('/')} />
      </div>

      <main className="max-w-3xl mx-auto px-6 py-12">
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-secondary/30 bg-secondary/10 text-secondary text-xs font-medium mb-4">
            <Sparkles className="w-3.5 h-3.5" />
            You've been invited
          </div>
          <h1 className="text-3xl sm:text-5xl font-heading text-white mb-2" data-testid="join-event-name">
            {event.name}
          </h1>
          {event.host_name && (
            <p className="text-muted-foreground">Hosted by <span className="text-white">{event.host_name}</span></p>
          )}
        </div>

        <div className="grid md:grid-cols-2 gap-6 mb-8">
          <Card className="bg-card border-white/10">
            <CardContent className="p-6 space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-muted-foreground text-sm flex items-center gap-2">
                  <Trophy className="w-4 h-4 text-secondary" />
                  Holes
                </span>
                <span className="text-white font-mono">{event.num_holes}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-muted-foreground text-sm flex items-center gap-2">
                  <CheckCircle2 className="w-4 h-4 text-primary" />
                  Total Par
                </span>
                <span className="text-white font-mono">{event.total_par}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-muted-foreground text-sm flex items-center gap-2">
                  <Users className="w-4 h-4 text-primary" />
                  Players joined
                </span>
                <span className="text-white font-mono">{event.participants_count || 0}</span>
              </div>
              {eventDate && (
                <div className="flex items-center justify-between">
                  <span className="text-muted-foreground text-sm flex items-center gap-2">
                    <Calendar className="w-4 h-4 text-primary" />
                    Date
                  </span>
                  <span className="text-white text-sm">{eventDate}</span>
                </div>
              )}
              {event.description && (
                <div className="pt-3 border-t border-white/5">
                  <p className="text-sm text-muted-foreground">{event.description}</p>
                </div>
              )}
            </CardContent>
          </Card>

          <Card className="bg-card border-white/10">
            <CardHeader>
              <CardTitle className="text-white font-heading text-lg">Join the event</CardTitle>
            </CardHeader>
            <CardContent>
              {member ? (
                <div className="space-y-4">
                  <p className="text-sm text-muted-foreground">
                    Logged in as <span className="text-white">{member.name}</span>.
                  </p>
                  <Button
                    onClick={handleJoin}
                    disabled={joining}
                    className="w-full bg-secondary text-black hover:bg-secondary/90"
                    data-testid="join-event-submit"
                  >
                    {joining ? 'Joining...' : 'Join this event'}
                    <ArrowRight className="ml-2 w-4 h-4" />
                  </Button>
                </div>
              ) : (
                <form onSubmit={handleJoin} className="space-y-3">
                  <div>
                    <Label className="text-white text-sm" htmlFor="j-name">Your name</Label>
                    <Input
                      id="j-name"
                      value={form.name}
                      onChange={(e) => setForm({ ...form, name: e.target.value })}
                      placeholder="Jane Doe"
                      className="bg-muted border-white/10 text-white"
                      data-testid="join-name-input"
                      required
                    />
                  </div>
                  <div>
                    <Label className="text-white text-sm" htmlFor="j-email">Email</Label>
                    <Input
                      id="j-email"
                      type="email"
                      value={form.email}
                      onChange={(e) => setForm({ ...form, email: e.target.value })}
                      placeholder="you@example.com"
                      className="bg-muted border-white/10 text-white"
                      data-testid="join-email-input"
                      required
                    />
                  </div>
                  <Button
                    type="submit"
                    disabled={joining}
                    className="w-full bg-secondary text-black hover:bg-secondary/90"
                    data-testid="join-event-submit"
                  >
                    {joining ? 'Creating your account…' : 'Join the event'}
                    <ArrowRight className="ml-2 w-4 h-4" />
                  </Button>
                  <p className="text-xs text-muted-foreground text-center pt-1">
                    No password needed — we'll create a TGA amateur account for you.
                  </p>
                </form>
              )}
            </CardContent>
          </Card>
        </div>
      </main>
    </div>
  );
}
